import React, { useEffect, useState } from 'react';
import { BiCog, BiHome, BiUser } from 'react-icons/bi';
import { MdPayment } from 'react-icons/md';
import { Outlet, useLocation, NavLink  } from 'react-router-dom';
import Payment from '../pages/mobile/payment';
import axios from 'axios';
import { rootUrl } from '../helpers';
import { useAuth } from '../context/auth-context';
import ProfilePage from '../pages/mobile/profile';
import Dash from '../pages/mobile/dash';
import { FaSpinner } from 'react-icons/fa';

const PhoneLayout = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [allData, setAllData] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const auth = useAuth();

  const getGen = async () => {
    try {
      const response = await axios.get(rootUrl(`/getGeneral/${auth.user?.id}`));
      if (response.data.status == 200) {
        setAllData(response.data.data);
        return true;
      }
      return false;
    } catch (e) {
      console.error("Error fetching general data:", e);
      return false;
    }
  };
  
  const fetchReceipts = async () => {
    try {
      const response = await axios.get(rootUrl(`/receipts/${auth.user?.id}`));
      if (response.data.status == 200) {
        setReceipts(response.data.data);
        return true;
      }
      return false;
    } catch (e) {
      console.error("Error fetching receipts:", e);
      return false;
    }
  };

  // Process receipts into grouped data
  const processReceipts = () => {
    if (!allData || !receipts || receipts.length === 0) {
      setGroupedData([]);
      return;
    }

    const grouped = Object.values(
      receipts.reduce((acc, item) => {
        if (!acc[item.voucher]) {
          acc[item.voucher] = {
            voucher: item.voucher,
            date: item.date,
            paye: allData.suppliers?.find(a => a.id == item.paye)?.name || 'Unknown',
            type: item.type,
            cid: item.cid,
            bank: item.bank,
            sof: item.sof,
            cheque: item.cheque,
            updatedBy: item.updatedBy,
            proj_id: item.proj_id,
            status: item.status,
            recon: item.recon,
            transactions: []
          };
        }
        acc[item.voucher].transactions.push({
          amount: item.amount,
          coa_id: allData.budget?.find(a => a.id == item.coa_id)?.coa || 'Unknown',
          description: item.description
        });
        return acc;
      }, {})
    );
    setGroupedData(grouped);
  };

  // Initialize tabs after data is loaded
  const initializeTabs = () => {
    if (!allData || !allData.users) return;

    const newTabs = [
      {
        id: 0,
        label: "Home",
        icon: <BiHome className="w-6 h-6" />,
        content: <Dash allData={allData} user={allData.users.find(a => a.id == auth.user?.id)} receipts={groupedData} />,
      },
      {
        id: 1,
        label: "Pay",
        icon: <MdPayment className="w-6 h-6" />,
        content: <Payment allData={allData} />,
      },
      {
        id: 2,
        label: "Profile",
        icon: <BiUser className="w-6 h-6" />,
        content: <ProfilePage allData={allData} groupedData={groupedData} />,
      },
    ];
    setTabs(newTabs);
  };

  // Load all data and wait for completion
  const loadAllData = async () => {
    setIsLoading(true);
    setDataLoaded(false);

    try {
      // Load both data sources concurrently
      const [genDataSuccess, receiptsSuccess] = await Promise.all([
        getGen(),
        fetchReceipts()
      ]);

      // Only proceed if both data sources loaded successfully
      if (genDataSuccess && receiptsSuccess) {
        setDataLoaded(true);
      } else {
        console.error("Failed to load required data");
        // Retry after a delay
        setTimeout(() => {
          loadAllData();
        }, 2000);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      // Retry after a delay
      setTimeout(() => {
        loadAllData();
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    if (auth.user?.id) {
      loadAllData();
    }
  }, [auth.user?.id]);

  // Process receipts when both allData and receipts are available
  useEffect(() => {
    if (allData && receipts && dataLoaded) {
      processReceipts();
    }
  }, [allData, receipts, dataLoaded]);

  // Initialize tabs when groupedData is ready
  useEffect(() => {
    if (allData && groupedData && dataLoaded) {
      initializeTabs();
    }
  }, [allData, groupedData, dataLoaded]);

  // Show loading spinner while data is being loaded
  if (isLoading || !dataLoaded || !allData || !receipts || tabs.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <FaSpinner className="spinner text-blue-600 text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Loading application...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we load your data</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Scrollable Content Area */}
      <main className="flex-1 overflow-y-auto pb-5 h-[85vh] scrollbar-none">
        {tabs[activeTab]?.content}
      </main>

      {/* Bottom Navigation */}
      <nav className="mt-5 fixed bottom-4 left-1/2 transform -translate-x-1/2 w-[90%] bg-white/50 backdrop-blur-lg shadow-lg rounded-2xl border border-gray-200">
        <div className="flex justify-around items-center p-3">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center p-3 rounded-xl transition-all duration-300 ${
                activeTab === tab.id
                  ? 'text-blue-600 bg-white shadow-md scale-105'
                  : 'text-gray-500 hover:text-gray-700 hover:scale-105'
              }`}
            >
              {tab.icon}
              <span className="text-xs font-medium mt-1">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default PhoneLayout;